import {
  NPC_BOX_FIRST_TICKS,
  CRATE_MASS,
  CRATE_RADIUS,
  CRATE_SCATTER_ATTEMPTS,
  CRATE_SCATTER_GAP,
  CRATE_SCATTER_START_CLEARANCE,
  PLAYER_MASS,
  PLAYER_RADIUS,
  SHOOTER_PHASE_TICKS,
  START_CHESS_CLOCK_MS,
  TILE,
} from './constants';
import { START_FEN } from './chess';
import type { ParsedLevel } from './levels';
import { isFree } from './obstacles';
import { drawInt, drawRange } from './rng';
import { mapHeight, mapWidth } from './tilemap';
import type { Crate, Player, PlayerId, State, Vec2 } from './types';
import { dist } from './vec';
import { placeInitialPickups } from './weapons';

function createPlayer(id: PlayerId, pos: Vec2): Player {
  return {
    id,
    pos: { ...pos },
    vel: { x: 0, y: 0 },
    radius: PLAYER_RADIUS,
    mass: PLAYER_MASS,
    chessClockMs: START_CHESS_CLOCK_MS,
    hitTimeLostMs: 0,
    weapon: null,
    invulnerableTicks: 0,
    connected: true,
  };
}

function createCrate(pos: Vec2): Crate {
  return { pos: { ...pos }, vel: { x: 0, y: 0 }, radius: CRATE_RADIUS, mass: CRATE_MASS };
}

/** May a scattered crate lie here? Clear of walls, pillars and other crates, away from starts. */
function crateFits(s: State, pos: Vec2): boolean {
  if (!isFree(s.map, s.pillars, pos.x, pos.y, CRATE_RADIUS + CRATE_SCATTER_GAP)) return false;
  for (const p of s.players) if (dist(pos, p.pos) < CRATE_SCATTER_START_CLEARANCE) return false;
  for (const c of s.crates) if (dist(pos, c.pos) < 2 * CRATE_RADIUS + CRATE_SCATTER_GAP) return false;
  return true;
}

/**
 * Scatters `pairs` x 2 crates from the state RNG, each with its point mirror, so a random layout is
 * still fair. Rejection sampling with a bounded number of draws per pair; a pair that finds no room
 * is skipped, which keeps the result deterministic for a seed.
 */
function scatterCrates(s: State, pairs: number): void {
  const w = mapWidth(s.map);
  const h = mapHeight(s.map);
  const inset = TILE + CRATE_RADIUS + CRATE_SCATTER_GAP;
  for (let i = 0; i < pairs; i++) {
    for (let attempt = 0; attempt < CRATE_SCATTER_ATTEMPTS; attempt++) {
      const pos = { x: Math.round(drawRange(s, inset, w - inset)), y: Math.round(drawRange(s, inset, h - inset)) };
      const mirror = { x: w - pos.x, y: h - pos.y };
      if (dist(pos, mirror) < 2 * CRATE_RADIUS + CRATE_SCATTER_GAP) continue;
      if (!crateFits(s, pos) || !crateFits(s, mirror)) continue;
      s.crates.push(createCrate(pos), createCrate(mirror));
      break;
    }
  }
}

/**
 * The state at tick 0 of a match on `level`; scattered crates, the first pickups and who plays white
 * are drawn from `seed` (the colours last, so a seed's arena layout does not depend on them).
 */
export function createInitialState(level: ParsedLevel, seed: number): State {
  const state: State = {
    tick: 0,
    rng: seed >>> 0,
    phase: 'shooter',
    phaseEndsAtTick: SHOOTER_PHASE_TICKS,
    transitionTicks: 0,
    winner: null,
    outcome: null,
    chess: { fen: START_FEN, white: 0, lastMove: null, lastMoveTick: 0 },
    arenaSetup: { starts: [{ ...level.starts[0] }, { ...level.starts[1] }], crates: level.crates.map(p => ({ ...p })), cratePairs: level.cratePairs },
    map: { ...level.map },
    pillars: level.pillars.map((p) => ({ pos: { ...p.pos }, radius: p.radius })),
    spawnSpots: level.weaponSpots.map((spot) => ({ pos: { ...spot.pos }, tier: spot.tier })),
    players: [createPlayer(0, level.starts[0]), createPlayer(1, level.starts[1])],
    crates: level.crates.map(createCrate),
    npcBoxes: [],
    npcs: [],
    nextNpcId: 0,
    nextNpcBoxTick: NPC_BOX_FIRST_TICKS,
    pickups: [],
    projectiles: [],
    bursts: [],
    beams: [],
    blasts: [],
    spawnPlan: [],
  };
  resetShooter(state);
  state.chess.white = drawInt(state, 2) as PlayerId;
  return state;
}

/** Rebuilds only arena resources; the board, clocks, connections and RNG stream survive. */
export function resetShooter(s: State): void {
  for (const player of s.players) {
    player.pos = { ...s.arenaSetup.starts[player.id] };
    player.vel = { x: 0, y: 0 };
    player.weapon = null;
    player.invulnerableTicks = 0;
  }
  s.npcBoxes = [];
  s.npcs = [];
  s.nextNpcId = 0;
  s.nextNpcBoxTick = s.tick + NPC_BOX_FIRST_TICKS;
  s.crates = s.arenaSetup.crates.map(createCrate);
  s.pickups = [];
  s.spawnPlan = [];
  s.projectiles = [];
  s.bursts = [];
  s.beams = [];
  s.blasts = [];
  scatterCrates(s, s.arenaSetup.cratePairs);
  placeInitialPickups(s);
}

/**
 * Deep copy of a state. step() mutates a copy and returns it, so the caller's state stays untouched
 * (prediction keeps old states around for reconciliation). Kept by hand instead of JSON round trips
 * because it runs every tick. Map, pillars and spots never change, so they are shared.
 */
export function cloneState(s: State): State {
  const vec = (v: Vec2): Vec2 => ({ x: v.x, y: v.y });
  return {
    tick: s.tick,
    rng: s.rng,
    phase: s.phase,
    phaseEndsAtTick: s.phaseEndsAtTick,
    transitionTicks: s.transitionTicks,
    winner: s.winner,
    outcome: s.outcome,
    chess: { ...s.chess, lastMove: s.chess.lastMove ? { ...s.chess.lastMove } : null },
    arenaSetup: s.arenaSetup,
    map: s.map,
    pillars: s.pillars,
    spawnSpots: s.spawnSpots,
    players: [
      { ...s.players[0], pos: vec(s.players[0].pos), vel: vec(s.players[0].vel) },
      { ...s.players[1], pos: vec(s.players[1].pos), vel: vec(s.players[1].vel) },
    ],
    nextNpcId: s.nextNpcId,
    nextNpcBoxTick: s.nextNpcBoxTick,
    npcBoxes: s.npcBoxes.map(b => ({ ...b, pos: vec(b.pos) })),
    npcs: s.npcs.map(n => ({ ...n, pos: vec(n.pos), vel: vec(n.vel), moveDir: vec(n.moveDir) })),
    crates: s.crates.map((c) => ({ ...c, pos: vec(c.pos), vel: vec(c.vel) })),
    pickups: s.pickups.map((p) => ({ ...p, pos: vec(p.pos) })),
    projectiles: s.projectiles.map((p) => ({ ...p, pos: vec(p.pos), vel: vec(p.vel) })),
    bursts: s.bursts.map((b) => ({ ...b, dir: vec(b.dir) })),
    beams: s.beams.map((b) => ({ ...b, struck: [...b.struck], from: vec(b.from), dir: vec(b.dir) })),
    blasts: s.blasts.map((b) => ({ ...b, pos: vec(b.pos) })),
    spawnPlan: s.spawnPlan.map((e) => ({ ...e, pos: vec(e.pos) })),
  };
}
